import { getLevel, getNextRow } from "./domHelpers.js";
import { isDescendant, isRowShowing, isSibling } from "./elementTests.js";
import {
	childrenShowingClass,
	isParentClass,
	toggleButtonClass,
} from "./selectorHelpers.js";
import { toggleRowVisibility } from "./visibility.js";

/**
 * Add the required listeners to the levels in a table.
 *
 * @param tbody The table to add the listeners to.
 */
export function addLevelListeners(tbody: HTMLElement): void {
	for (let level = 0; level < 10; level++) {
		const levelElements = getLevel(tbody, level);
		if (levelElements) {
			addLevelListener(levelElements, level);
		} else {
			break;
		}
	}
}

/**
 * Add the listeners to a level in the table.
 *
 * @param elements The rows at the current level.
 * @param level The level number.
 */
function addLevelListener(
	elements: NodeListOf<HTMLTableRowElement>,
	level: number,
) {
	elements.forEach((tr) => addPageListener(tr, level));
}

/**
 * Adds the listeners for a page in the list of WordPress pages.
 *
 * @param row The row that represents the page.
 * @param level The currrrent level of the tree.
 */
function addPageListener(row: HTMLTableRowElement, level: number) {
	assignParentClass(row, level);
}

/**
 * Handles the click of a row.
 *
 * @param row The row to handle.
 * @param level The current level index.
 */
function handleRowClick(row: HTMLTableRowElement, level: number) {
	row.classList.toggle(childrenShowingClass);
	handleButtonText(row);
	let next = getNextRow(row);
	while (next) {
		const showing = isRowShowing(next);
		if (isSibling(next, level)) {
			break;
		} else if (isDescendant(next, level) || showing) {
			toggleRowVisibility(next);
		}
		next = getNextRow(next);
	}
}

/**
 * Assigns the parent class and adds toggles for a row if it has children.
 *
 * @param row The row to operate on.
 * @param level The current level in the tree.
 * @throws Error if the checkbox that we're relying on for positioning is
 *   missing.
 */
function assignParentClass(row: HTMLTableRowElement, level: number) {
	const nextSibling = getNextRow(row);
	const cell = document.createElement("td");
	row.prepend(cell);
	if (nextSibling && isDescendant(nextSibling, level)) {
		row.classList.add(isParentClass);
		const rowToggle = row.querySelector(".check-column");
		if (!rowToggle) {
			throw new Error("Page checkbox not found to append after");
		}
		const button = document.createElement("button");
		button.classList.add(toggleButtonClass);
		button.textContent = "+";
		button.type = "button";
		button.addEventListener("click", () => handleRowClick(row, level), {});
		cell.append(button);
	}
}

/**
 * Set's the text content of the toggle button based on its state
 *
 * @param row The row to use.
 */
export function handleButtonText(row: HTMLTableRowElement): void {
	const button = row.querySelector("." + toggleButtonClass);
	if (!button) return;
	if (row.classList.contains(childrenShowingClass)) {
		button.textContent = "-";
	} else {
		button.textContent = "+";
	}
}
